"use client";

import { useActivateAccount } from "@/features/auth/hooks/useActivateAccount";
import { ArrowLeft, CheckCircle2, KeyRound, Lock, LogIn, Mail } from "lucide-react";
import Link from "next/link";

export default function ActivatePage() {
  const {
    step,
    identifier,
    otp,
    password,
    confirmPassword,
    isLoading,
    setIdentifier,
    setOtp,
    setPassword,
    setConfirmPassword,
    handleSendOtp,
    handleVerifyOtp,
    handleSetPassword,
    handleBackToIdentifier,
  } = useActivateAccount();

  const renderProgress = () => {
    const steps = [
      { name: "Verify", active: step === "identifier" || step === "otp" || step === "password" || step === "success" },
      { name: "Code", active: step === "otp" || step === "password" || step === "success" },
      { name: "Password", active: step === "password" || step === "success" },
    ];

    return (
      <div className="flex items-center justify-between w-full max-w-xs mx-auto mb-8">
        {steps.map((s, idx) => (
          <div key={s.name} className="flex items-center flex-1 last:flex-initial">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                s.active
                  ? "bg-[var(--accent)] text-[var(--text-inverse)] ring-4 ring-[var(--accent-soft)]"
                  : "bg-[var(--surface-muted)] text-[var(--text-muted)] border border-[var(--border)]"
              }`}
            >
              {idx + 1}
            </div>
            {idx < steps.length - 1 && (
              <div
                className={`h-0.5 flex-1 mx-2 transition-all duration-300 ${
                  steps[idx + 1].active ? "bg-[var(--accent)]" : "bg-[var(--border)]"
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <main className="min-h-screen bg-[var(--page)] flex items-center justify-center px-4 py-12 text-[var(--text)] sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-[var(--surface)] border border-[var(--border)] p-8 rounded-[var(--radius-lg)] shadow-[var(--shadow-md)]">
        <div className="text-center">
          <h2 className="text-2xl font-bold tracking-tight text-[var(--text-strong)]">
            Activate Your Account
          </h2>
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            {step === "identifier" && "Enter your registered email or phone to start activation."}
            {step === "otp" && "Enter the verification code sent to your registered contact."}
            {step === "password" && "Create a secure password to complete your account setup."}
            {step === "success" && "Your account has been activated successfully!"}
          </p>
        </div>

        {step !== "success" && renderProgress()}

        {step === "identifier" && (
          <form onSubmit={handleSendOtp} className="space-y-6">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[var(--text)]">Email or Phone</span>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]" />
                <input
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  type="text"
                  required
                  disabled={isLoading}
                  className="ui-focus h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] pl-10 pr-3 text-sm text-[var(--text)] placeholder:text-[var(--text-subtle)] disabled:opacity-70"
                  placeholder="you@company.com or +919876543210"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--text-inverse)] transition hover:bg-[var(--accent-hover)] disabled:opacity-70"
            >
              {isLoading ? "Sending..." : "Send Verification Code"}
            </button>

            <div className="text-center text-sm">
              <Link href="/login" className="inline-flex items-center gap-1 font-medium text-[var(--accent)] hover:underline">
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Link>
            </div>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div className="rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-muted)] px-3 py-2 text-sm text-[var(--text-muted)]">
              Code sent to <span className="font-medium text-[var(--text)]">{identifier}</span>
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[var(--text)]">Verification Code</span>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]" />
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                  pattern="\d{4,6}"
                  maxLength={6}
                  disabled={isLoading}
                  className="ui-focus h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] pl-10 pr-3 text-sm tracking-[0.3em] text-[var(--text)] placeholder:tracking-normal placeholder:text-[var(--text-subtle)] disabled:opacity-70"
                  placeholder="123456"
                />
              </div>
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleBackToIdentifier}
                disabled={isLoading}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-4 text-sm font-semibold text-[var(--text)] transition hover:bg-[var(--surface-muted)] disabled:opacity-70"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex h-10 flex-[1.4] items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--text-inverse)] transition hover:bg-[var(--accent-hover)] disabled:opacity-70"
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </button>
            </div>

            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => handleSendOtp()}
                disabled={isLoading}
                className="font-medium text-[var(--accent)] hover:underline bg-transparent border-0 cursor-pointer disabled:opacity-50"
              >
                Resend activation code
              </button>
            </div>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={handleSetPassword} className="space-y-6">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[var(--text)]">Choose Password</span>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]" />
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  required
                  disabled={isLoading}
                  placeholder="Minimum 8 characters"
                  className="ui-focus h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] pl-10 pr-3 text-sm text-[var(--text)] placeholder:text-[var(--text-subtle)] disabled:opacity-70"
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[var(--text)]">Confirm Password</span>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]" />
                <input
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  required
                  disabled={isLoading}
                  placeholder="Re-enter your password"
                  className="ui-focus h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] pl-10 pr-3 text-sm text-[var(--text)] placeholder:text-[var(--text-subtle)] disabled:opacity-70"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--text-inverse)] transition hover:bg-[var(--accent-hover)] disabled:opacity-70"
            >
              {isLoading ? "Activating..." : "Activate Account & Sign In"}
            </button>
          </form>
        )}

        {step === "success" && (
          <div className="text-center py-6 space-y-4">
            <div className="flex justify-center">
              <CheckCircle2 className="h-16 w-16 text-[var(--success)] animate-bounce" />
            </div>
            <h3 className="text-lg font-bold text-[var(--text-strong)]">Account Activated!</h3>
            <p className="text-sm text-[var(--text-muted)]">
              Redirecting you to your dashboard...
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
